/**
 * Presentation rules for reconstructed commercial history.
 *
 * A Billing Specification is either evidenced by a real invoice, or it
 * is an operator-approved inference for a period whose invoice was
 * never found. The database already records that distinction — in
 * `source_reference` and in the specification notes — but until now the
 * Billing UI rendered neither, so an inferred term was indistinguishable
 * from a documented one. These helpers exist so that can't happen again.
 *
 * Deliberately client-agnostic: nothing here knows about any particular
 * customer. The signal is always the stored provenance, never who the
 * client is.
 */

/** Marker an operator writes into `source_reference` / notes when a
 *  period is reconstructed by inference rather than from an invoice. */
export const INFERENCE_MARKER = "SUPPORTED_INFERENCE";

/** Settled records are commercial history, not proposals in flight. */
export const HISTORICAL_STATUSES = ["superseded", "cancelled"];
export const SETTLED_STATUSES = ["active", "superseded", "cancelled"];

const round2 = (n) => Math.round(n * 100) / 100;

export function isInferredSource(sourceReference) {
  return (
    typeof sourceReference === "string" &&
    sourceReference.toUpperCase().includes(INFERENCE_MARKER)
  );
}

/**
 * How a specification's source should read in a list.
 *
 * Never abbreviates an inference to something that could be mistaken
 * for an invoice number — the whole point is that no invoice exists.
 */
export function describeSource(spec) {
  const ref = spec?.source_reference;
  if (!ref) return { text: "Not recorded", inferred: false, present: false };
  return { text: String(ref), inferred: isInferredSource(ref), present: true };
}

export function isHistoricalSpec(status) {
  return HISTORICAL_STATUSES.includes(status);
}

/** True once a specification is agreed commercial truth — active or
 *  historical. Such a record is never a "proposal". */
export function isSettledSpec(status) {
  return SETTLED_STATUSES.includes(status);
}

/**
 * The invoice date is `billing_basis_date` — the date the source
 * invoice was issued.
 *
 * A period start is NOT an invoice date, and an inferred term has no
 * invoice date at all, so both must render as "not documented" rather
 * than borrowing a date from somewhere else.
 */
export function invoiceDateOf(row) {
  const d = row?.billing_basis_date || row?.original_invoice_date;
  return d ? String(d).slice(0, 10) : null;
}

/**
 * Year-over-year comparison for one specification.
 *
 * Prefers `prior_recurring_annual_value` when the renewal engine
 * recorded one. Reconstructed history has no stored prior, so fall back
 * to the chronologically preceding specification's own annual value.
 * Both sides are `recurring_annual_value`, which is already net of
 * recurring credits, so the two bases are directly comparable.
 *
 * Returns nulls rather than a fabricated 0% where there is nothing
 * meaningful to compare — the opening term, or a zero prior.
 */
export function comparePeriod(spec, priorSpec) {
  const stored = spec?.prior_recurring_annual_value;
  const prior =
    stored != null
      ? Number(stored)
      : priorSpec?.recurring_annual_value != null
        ? Number(priorSpec.recurring_annual_value)
        : null;
  const current =
    spec?.recurring_annual_value != null ? Number(spec.recurring_annual_value) : null;

  if (
    prior == null || current == null ||
    !Number.isFinite(prior) || !Number.isFinite(current)
  ) {
    return { prior: null, change: null, pct: null, derived: false };
  }
  const change = round2(current - prior);
  return {
    prior,
    change,
    // A percentage against a zero base states nothing useful.
    pct: prior === 0 ? null : round2((change / prior) * 100),
    derived: stored == null,
  };
}

/**
 * Recurring rows seeded by activating a Billing Specification are
 * derived commercial history, not hand-entered scratch data. Deleting
 * one would silently desynchronise the ledger from the specification
 * that produced it, so the ordinary Billing UI must not offer it.
 * Manually added rows stay deletable.
 */
export function canPermanentlyDelete(item) {
  return (item?.source_type || "manual") !== "spec";
}

/**
 * Whether "Generate proposal" should present as an available action.
 *
 * Mirrors the backend renewal guard rather than second-guessing it: if
 * renewal readiness says the client is held, the UI must not offer an
 * action the server will refuse.
 */
export function proposalAvailability({ readiness, hasActiveItems, isAdmin }) {
  if (!isAdmin) return { allowed: false, reason: null };
  if (!hasActiveItems) {
    return { allowed: false, reason: "No active recurring items to propose from." };
  }
  // Readiness not loaded yet — stay conservative rather than flashing an
  // action that may be withdrawn a moment later.
  if (!readiness) return { allowed: false, reason: null };
  if (readiness.renewal_ready === false) {
    const first = (readiness.reasons || [])[0];
    return {
      allowed: false,
      reason: first
        ? `Renewal held — ${first.charAt(0).toLowerCase()}${first.slice(1)}`
        : "Renewal held until the commercial baseline is validated.",
    };
  }
  return { allowed: true, reason: null };
}

/**
 * Oldest-first ordering by period, so each term can be compared with
 * the one that actually preceded it regardless of how the API sorted.
 */
export function chronological(specs) {
  return [...(specs || [])].sort((a, b) =>
    String(a.period_start || "").localeCompare(String(b.period_start || "")),
  );
}

/**
 * Attach the year-over-year comparison to every specification, using
 * each one's true predecessor.
 */
export function withComparisons(specs) {
  const ordered = chronological(specs);
  return ordered.map((s, i) => ({ ...s, comparison: comparePeriod(s, ordered[i - 1]) }));
}
